import { useState, useEffect, useCallback } from 'react';
import { ChevronUp, Loader2 } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LiveExecutionHeader } from './LiveExecutionHeader';
import { ExecutedNodesList } from './ExecutedNodesList';
import { NodeDataPanel } from './NodeDataPanel';
import { LogsPanel } from './LogsPanel';
import { useLiveExecutionContext } from './LiveExecutionContext';
import { executionsApi } from '@/lib/api';
import { useWorkflowStore } from '@/stores/workflow-store';
import type { LogEntry } from './useLiveExecution';

interface ExecutionProgress {
  status: 'idle' | 'running' | 'success' | 'failed';
  progress: number;
  currentNode?: string;
  totalNodes: number;
  completedNodes: number;
}

interface LiveExecutionPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  executionProgress?: ExecutionProgress;
}

export function LiveExecutionPanel({
  isOpen,
  onToggle,
  executionProgress,
}: LiveExecutionPanelProps) {
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [selectedNodeFullData, setSelectedNodeFullData] = useState<{
    inputData?: unknown;
    outputData?: unknown;
    logs?: LogEntry[];
  } | null>(null);
  const [loadingNodeData, setLoadingNodeData] = useState(false);

  // Use shared context state (persists across panel open/close)
  const {
    status,
    nodeExecutions,
    nodesLatestFirst,
    selectedNode,
    selectNode,
    stats,
    duration,
    logs,
    isLoading,
    executionId,
    completedAt,
  } = useLiveExecutionContext();

  // Canvas highlighting
  const setHighlightedNodeId = useWorkflowStore((state) => state.setHighlightedNodeId);

  // Handle node selection with canvas highlighting
  const handleSelectNode = useCallback(
    (nodeId: string) => {
      selectNode(nodeId);

      // Find the selected node to get its actual nodeId (for canvas highlighting)
      const node = nodeExecutions.find((n) => n.id === nodeId);
      if (node) {
        // Highlight the node on the canvas (use the actual canvas nodeId)
        setHighlightedNodeId(node.nodeId);
      }
    },
    [selectNode, nodeExecutions, setHighlightedNodeId]
  );

  // Clear canvas highlighting when panel closes or execution ends
  useEffect(() => {
    if (!isOpen) {
      setHighlightedNodeId(null);
    }
  }, [isOpen, setHighlightedNodeId]);

  // Fetch full node data when a node is selected
  const fetchNodeFullData = useCallback(async (nodeExecId: string) => {
    if (!executionId) return;

    try {
      setLoadingNodeData(true);
      const response = await executionsApi.getNodeExecutionById(executionId, nodeExecId);
      const fullNode = response.data;
      if (fullNode) {
        // Extract and normalize logs from the node execution
        const normalizeLevel = (level: string): 'log' | 'info' | 'warn' | 'error' => {
          const l = (level || 'info').toLowerCase();
          if (l === 'error') return 'error';
          if (l === 'warn' || l === 'warning') return 'warn';
          if (l === 'info') return 'info';
          return 'log';
        };

        const nodeLogs = Array.isArray(fullNode.logs) ? fullNode.logs.map((log: any, index: number) => ({
          id: log.id || `log-${nodeExecId}-${index}`,
          nodeId: fullNode.node_id || nodeExecId,
          level: normalizeLevel(log.level),
          message: log.message || (typeof log === 'string' ? log : JSON.stringify(log)),
          timestamp: typeof log.timestamp === 'number' ? log.timestamp : new Date(log.timestamp).getTime(),
        })) : [];

        setSelectedNodeFullData({
          inputData: fullNode.input_data
            ? typeof fullNode.input_data === 'string'
              ? JSON.parse(fullNode.input_data)
              : fullNode.input_data
            : undefined,
          outputData: fullNode.output_data
            ? typeof fullNode.output_data === 'string'
              ? JSON.parse(fullNode.output_data)
              : fullNode.output_data
            : (fullNode.output || undefined),
          logs: nodeLogs,
        });
      }
    } catch (error) {
      console.error('Failed to fetch node full data:', error);
      setSelectedNodeFullData(null);
    } finally {
      setLoadingNodeData(false);
    }
  }, [executionId]);

  // Fetch data when selected node changes
  useEffect(() => {
    if (selectedNode?.id) {
      // For sub-nodes (tool executions), use the in-memory data only
      // since they don't have database records
      if (selectedNode.isSubNode) {
        setSelectedNodeFullData({
          inputData: selectedNode.inputData,
          outputData: selectedNode.outputData,
        });
        setLoadingNodeData(false);
        return;
      }

      // First, use data from the node itself if available
      if (selectedNode.inputData || selectedNode.outputData) {
        setSelectedNodeFullData({
          inputData: selectedNode.inputData,
          outputData: selectedNode.outputData,
        });
      }
      // Then fetch full data from API for main nodes
      fetchNodeFullData(selectedNode.id);
    } else {
      setSelectedNodeFullData(null);
    }
  }, [selectedNode?.id, selectedNode?.isSubNode, selectedNode?.inputData, selectedNode?.outputData, fetchNodeFullData]);

  // Calculate progress
  const getProgress = () => {
    if (executionProgress) {
      return Math.round(executionProgress.progress);
    }
    if (status === 'success') return 100;
    if (stats.totalCount === 0) return status === 'running' ? 5 : 0;
    return Math.max(5, Math.round((stats.completedCount / stats.totalCount) * 100));
  };

  const getStatusColor = (s: string) => {
    switch (s) {
      case 'running':
        return 'bg-blue-500';
      case 'success':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Collapsed state - compact dock
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 w-96 border border-border bg-background shadow-lg rounded-lg z-50">
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-sm font-medium">Execution</span>
            {executionId ? (
              <>
                <div className="flex items-center gap-2">
                  <div className={cn('h-2 w-2 rounded-full', getStatusColor(status))} />
                  <span className="text-xs text-muted-foreground capitalize">
                    {status}
                  </span>
                </div>
                {(status === 'running' || status === 'success') && (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all duration-300',
                          status === 'success' ? 'bg-green-500' : 'bg-blue-500'
                        )}
                        style={{ width: `${getProgress()}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {getProgress()}%
                    </span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-xs text-muted-foreground">No active execution</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onToggle} className="h-7 w-7 p-0">
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Expanded state - full panel
  return (
    <div className="border-t bg-background flex flex-col h-full">
      {/* Header */}
      <LiveExecutionHeader
        status={status}
        duration={duration}
        completedAt={completedAt}
        stats={stats}
        onToggle={onToggle}
      />

      {/* Main Content - Resizable Panels */}
      <div className="flex-1 overflow-hidden relative">
        {isLoading && nodeExecutions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center h-full">
            <div className="text-center space-y-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Loading execution data...</p>
            </div>
          </div>
        ) : !executionId ? (
          <div className="flex-1 flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">No active execution</p>
          </div>
        ) : (
          <PanelGroup direction="horizontal" className="h-full" style={{ overflow: 'hidden' }}>
            {/* Left Panel: Nodes List */}
            <Panel defaultSize={20} minSize={15} maxSize={35} className="!overflow-hidden relative">
              <div className="absolute inset-0 bg-muted/20 flex flex-col overflow-hidden">
                <div className="border-b px-4 py-2 flex-shrink-0">
                  <h3 className="text-sm font-semibold">Nodes</h3>
                </div>
                <ExecutedNodesList
                  nodes={nodesLatestFirst}
                  selectedNodeId={selectedNode?.id || null}
                  onSelectNode={handleSelectNode}
                />
              </div>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors cursor-col-resize" />

            {/* Middle Panel: Input */}
            <Panel defaultSize={40} minSize={20} className="!overflow-hidden relative">
              <div className="absolute inset-0 border-r overflow-hidden">
                <NodeDataPanel
                  title="Input"
                  data={selectedNodeFullData?.inputData}
                  isLoading={loadingNodeData}
                  emptyMessage={selectedNode ? 'No input data' : 'Select a node to view data'}
                />
              </div>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors cursor-col-resize" />

            {/* Right Panel: Output (with Logs overlay) */}
            <Panel defaultSize={40} minSize={20} className="!overflow-hidden">
              <div className="h-full relative overflow-hidden" style={{ contain: 'strict' }}>
                {/* Output Panel - pr-10 accounts for collapsed Logs strip width */}
                <div
                  className={cn(
                    'h-full transition-opacity duration-300',
                    logsExpanded ? 'opacity-0 pointer-events-none' : 'pr-10'
                  )}
                >
                  <NodeDataPanel
                    title="Output"
                    data={selectedNodeFullData?.outputData || selectedNode?.outputData}
                    isLoading={loadingNodeData && !selectedNode?.outputData}
                    emptyMessage={
                      selectedNode?.status === 'running'
                        ? 'Node is still running...'
                        : selectedNode?.error
                        ? `Error: ${selectedNode.error}`
                        : selectedNode
                        ? 'No output data'
                        : 'Select a node to view data'
                    }
                  />
                </div>

                {/* Logs Panel (expands to overlay output) */}
                <LogsPanel
                  logs={selectedNodeFullData?.logs?.length ? selectedNodeFullData.logs : logs}
                  expanded={logsExpanded}
                  onToggle={() => setLogsExpanded(!logsExpanded)}
                  nodeLabel={selectedNode?.nodeLabel || selectedNode?.nodeType}
                />
              </div>
            </Panel>
          </PanelGroup>
        )}
      </div>
    </div>
  );
}
